import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ShieldCheck, BadgeCheck, Lock, CircleCheck,
  X, ExternalLink, MapPin, Calendar, Users, Plane, Bed, Car, Sparkles, ArrowRight,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import usePartners from '../../hooks/usePartners';

/**
 * RedirectOverlay v2 — Premium affiliate-transition experience.
 *
 * Design philosophy:
 *   The redirect is the SINGLE HIGHEST-CONVERSION moment in the funnel.
 *   The old spinner-and-please-wait screen throws away that moment. This
 *   version turns it into a reassurance beat: "Here's exactly what you
 *   picked, here's the official partner, here's why it's safe."
 *
 * References: Booking.com pre-redirect summary, Hopper "You're going to…"
 * screen, Apple pay authorization flow, Airbnb checkout confirmation.
 *
 * Contract:
 *   - Same-tab redirect (mobile-safe). Timer lives in useBookingFlow.
 *   - Always dismissible (X in the top-right); user is never trapped.
 *   - `context` is optional — component degrades gracefully to a generic
 *     "securing your deal" state when meta is absent.
 */

/**
 * Local icon map keyed by partner *type*. We keep icons in the UI (lucide-react
 * icons can't be shipped from a Python registry). Everything ELSE — name,
 * tagline, trust bullets, verified flags — comes from the backend /api/partners
 * registry via usePartners().
 */
const TYPE_ICON = {
  flight:       Plane,
  hotel:        Bed,
  car:          Car,
  transfer:     Car,
  train:        Plane,        // no lucide train; Plane reads as "transport"
  ferry:        Plane,
  multi_modal:  Plane,
  experience:   Sparkles,
  trip:         Sparkles,
};

function fmtDate(iso, locale) {
  if (!iso) return null;
  try {
    const day = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(iso) ? `${iso}T12:00:00` : iso;
    const parsed = new Date(day);
    return Number.isNaN(parsed.getTime()) ? iso
      : parsed.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

/** Format the displayed price. Accepts number, "€199", "€120-€160/night" */
function fmtPrice(context) {
  if (context?.priceDisplay) return String(context.priceDisplay);
  if (typeof context?.price === 'number') return `€${context.price.toFixed(0)}`;
  return null;
}

export default function RedirectOverlay({ isVisible, pendingUrl, context, onDismiss }) {
  const { t, language } = useLanguage();
  const { getPartner } = usePartners();
  const [showFallback, setShowFallback] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setShowFallback(false);
      setProgress(0);
      return undefined;
    }
    let raf;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(92, (elapsed / 900) * 92);
      setProgress(p);
      if (elapsed < 900) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const fallbackTimer = setTimeout(() => setShowFallback(true), 1800);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fallbackTimer);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const partner = getPartner(context?.partner);
  const PartnerIcon = TYPE_ICON[partner?.type] || Sparkles;
  const locale = language === 'bg' ? 'bg-BG' : 'en-US';
  const price = fmtPrice(context);
  const type = context?.type;
  const dest = context?.destination;
  const origin = context?.origin;
  const itemName = context?.itemName;
  const routeLabel = origin && dest ? `${origin} → ${dest}` : dest || origin || null;

  // Provider-aware trust bullets (from /api/partners registry).
  // The Icon rotation cycles between the three trust semantics so users
  // read three distinct visual signals rather than three checkmarks.
  const bulletIcons = [BadgeCheck, Lock, ShieldCheck];
  const trustBullets = (partner?.trust_bullets || []).slice(0, 3).map((text, i) => ({
    icon: bulletIcons[i] || CircleCheck,
    text,
  }));

  return createPortal(
    <div
      data-testid="redirect-overlay"
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md animate-in fade-in p-0 sm:p-4"
      role="status"
      aria-live="polite"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 sm:zoom-in-95 relative">
        {/* Escape hatch */}
        {onDismiss && (
          <button
            data-testid="redirect-overlay-close"
            onClick={onDismiss}
            aria-label={t('common.close')}
            className="absolute top-3.5 right-3.5 z-10 p-1.5 rounded-full bg-white/80 hover:bg-gray-100 transition text-gray-500 hover:text-gray-900"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Header — deep navy so the redirect feels like a formal handover */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-900 to-blue-950 px-6 py-5 text-white">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-cyan-300">
            <Sparkles className="w-3 h-3" />
            {language === 'bg' ? 'Продължаваме към партньора' : 'Continue to the travel partner'}
          </div>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/15 flex items-center justify-center flex-shrink-0">
              <PartnerIcon className="w-5 h-5 text-cyan-300" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <div className="text-lg sm:text-xl font-extrabold leading-tight truncate">{partner.name}</div>
                {partner?.verified && (
                  <BadgeCheck className="w-4 h-4 text-cyan-300 flex-shrink-0" aria-label="Verified partner" />
                )}
              </div>
              <div className="text-[11px] text-blue-100/70 truncate">{partner.tagline}</div>
            </div>
          </div>
          {/* Determinate progress bar — signals real motion */}
          <div className="mt-4 h-1 w-full rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-cyan-300 transition-[width] duration-100 ease-out"
              style={{ width: `${progress}%` }}
              data-testid="redirect-overlay-progress"
            />
          </div>
          <div className="mt-1.5 text-[11px] text-blue-100/70">
            {language === 'bg' ? 'Отваряме сайта на доставчика…' : 'Opening the provider website…'}
          </div>
        </div>

        {/* Trip summary — degrades gracefully if any field is missing */}
        {(routeLabel || itemName || context?.checkin || context?.departureDate || price) && (
          <div className="px-6 py-4 border-b border-gray-100 space-y-2" data-testid="redirect-overlay-summary">
            {routeLabel && (
              <SummaryRow
                icon={type === 'flight' ? <Plane className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                label={type === 'flight' ? (t('redirect.route') || 'Route') : (t('redirect.destination') || 'Destination')}
                value={routeLabel}
              />
            )}
            {itemName && (
              <SummaryRow
                icon={type === 'hotel' ? <Bed className="w-4 h-4" /> : type === 'flight' ? <Plane className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                label={type === 'hotel' ? (t('redirect.hotel') || 'Hotel') : type === 'flight' ? (t('redirect.airline') || 'Airline') : (t('redirect.item') || 'Selection')}
                value={itemName}
              />
            )}
            {(context?.checkin || context?.departureDate) && (
              <SummaryRow
                icon={<Calendar className="w-4 h-4" />}
                label={type === 'flight' ? (t('redirect.dates') || 'Dates') : (t('redirect.stay') || 'Stay')}
                value={
                  type === 'flight'
                    ? [fmtDate(context.departureDate, locale), fmtDate(context.returnDate, locale)].filter(Boolean).join(' → ') || '—'
                    : [fmtDate(context.checkin, locale), fmtDate(context.checkout, locale)].filter(Boolean).join(' → ') || '—'
                }
              />
            )}
            {(context?.guests || context?.passengers) && (
              <SummaryRow
                icon={<Users className="w-4 h-4" />}
                label={t('redirect.travellers') || 'Travellers'}
                value={
                  type === 'flight'
                    ? `${context.passengers || 1} ${(context.passengers || 1) > 1 ? (t('common.travelers') || 'travellers') : (t('common.traveler') || 'traveller')}`
                    : `${context.guests || 2} ${(context.guests || 2) > 1 ? (t('common.guests') || 'guests') : (t('common.guest') || 'guest')} · ${context.rooms || 1} ${(context.rooms || 1) > 1 ? (t('common.rooms') || 'rooms') : (t('common.room') || 'room')}`
                }
              />
            )}
            {price && (
              <div className="pt-2 mt-2 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                  {t('redirect.displayedPrice') || 'Displayed price'}
                </span>
                <span
                  className="text-lg sm:text-xl font-extrabold text-blue-700 tabular-nums"
                  data-testid="redirect-overlay-price"
                >
                  {price}
                </span>
              </div>
            )}
            {price && (
              <p className="text-[10px] text-gray-400 leading-relaxed">
                {t('redirect.priceCaveat') || 'Final price is confirmed on the partner site before you pay.'}
              </p>
            )}
          </div>
        )}

        {/* Trust bullets */}
        <div className="px-6 py-4 space-y-2" data-testid="redirect-overlay-trust">
          {trustBullets.map(({ icon: I, text }) => (
            <div key={text} className="flex items-center gap-2.5 text-sm text-gray-700">
              <span className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <I className="w-3.5 h-3.5 text-emerald-600" />
              </span>
              <span className="font-medium">{text}</span>
              <CircleCheck className="w-3.5 h-3.5 text-emerald-500 ml-auto flex-shrink-0" />
            </div>
          ))}
        </div>

        {/* Manual fallback (browser slow / blocked) — always canonical CTA */}
        {showFallback && pendingUrl && (
          <div className="px-6 pb-3">
            <a
              data-testid="redirect-overlay-fallback-link"
              href={pendingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-md shadow-blue-600/25 hover:shadow-lg hover:shadow-blue-600/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all min-h-[44px]"
            >
              <ExternalLink className="w-4 h-4" />
              {t('booking.tapToContinue') || `Open ${partner.name}`}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Do not display fake "saved" or "price alert created" controls.
            Real notifications require the user's permission and persistence. */}

        {/* Fine print — real partner disclosure, never fake urgency */}
        <p
          data-testid="redirect-overlay-partner-note"
          className="px-6 pb-5 text-[10px] text-gray-400 leading-relaxed text-center"
        >
          {language === 'bg'
            ? 'Все още няма потвърдена резервация. Проверете датите, крайната цена и условията на сайта на партньора, преди да платите.'
            : 'No reservation has been confirmed yet. Check dates, final prices and conditions on the partner site before payment.'}
        </p>
      </div>
    </div>,
    document.body,
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-gray-400 flex-shrink-0">{icon}</span>
      <span className="text-gray-500 text-[11px] uppercase tracking-widest font-semibold flex-shrink-0 w-24">
        {label}
      </span>
      <span className="text-gray-900 font-semibold truncate flex-1 text-right">{value}</span>
    </div>
  );
}
